import React, { useCallback, useEffect, useState } from 'react';
import Dialog from '../../../components/Dialog';
import { useTypeToConfirm } from '../../../components/TypeToConfirm';
import { IconTrash } from '../../../components/dialogIcons';
import { takeEverything, lastFullCopy, copyAge, EVERYTHING } from '../../../lib/dataExport';
import {
  loadYears, saveYears, loadDue, runRemoval,
  totalDue, dueSentence, removedSentence, KEPT, REMOVED, DEFAULT_YEARS,
} from '../../../lib/retention';

/*
  Settings -> Data. Two panes, both new on 7 September 2026 for job 17.

  The owner asked for three things and these are all three of them:

  - **"I can pull/export all the data at anytime."** "Take everything out"
    below. One press, one spreadsheet, every table in the site, with a receipt
    on the front page saying what should be in it.
  - **"Setting up a back up copy."** That is the same file, kept somewhere he
    controls. The pane says when the last one was taken and when it has gone
    stale, because `docs/BACKUPS.md` is blunt about the honest version: every
    trigger for taking a copy depends on somebody remembering.
  - **"After 3 years the data is removed."** "How long things are kept" below.

  **These are two panes on Site Settings rather than a new screen**, because
  every screen in OnHand was drawn and signed off between 28 August and 5
  September and a new one would be a screen nobody drew. Site Settings is a rail
  of categories with a form beside it, which is the approved shape in
  `docs/design/group-d/`, and this is two more categories in it.
*/

function Figure({ n, label, dot }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, whiteSpace: 'nowrap' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, alignSelf: 'center', flexShrink: 0 }} />
      <b style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{typeof n === 'number' ? n.toLocaleString() : n}</b>
      <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{label}</span>
    </span>
  );
}

function Note({ tone = 'gold', children }) {
  const map = {
    gold: ['var(--warning-bg)', 'var(--warning-ink)'],
    good: ['var(--success-bg)', 'var(--success-ink)'],
    bad:  ['var(--critical-bg)', 'var(--critical-ink)'],
  };
  const [bg, fg] = map[tone] || map.gold;
  return (
    <div style={{ marginTop: 16, borderRadius: 12, padding: '13px 15px', fontSize: 13, lineHeight: 1.55, background: bg, color: fg }}>
      {children}
    </div>
  );
}

function Bullets({ title, items, dot }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.3, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 8 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 7 }}>
        {items.map(t => (
          <li key={t} style={{ display: 'flex', gap: 9, fontSize: 13, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, marginTop: 6, flexShrink: 0 }} />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* --------------------------- Take everything out --------------------------- */

export function FullCopyPane({ company, readOnly, profile, toast }) {
  const [busy, setBusy]   = useState(false);
  const [step, setStep]   = useState(null);
  const [last, setLast]   = useState(undefined);   // undefined while loading
  const [years, setYears] = useState(DEFAULT_YEARS);
  const [result, setResult] = useState(null);

  const refresh = useCallback(async () => {
    const [copy, y] = await Promise.all([lastFullCopy(), loadYears()]);
    setLast(copy.when || null);
    setYears(y.years);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const age = copyAge(last?.created_at);
  const lastRows = last?.details?.rows ?? null;

  async function take() {
    setBusy(true);
    setResult(null);
    try {
      const out = await takeEverything({
        company, years, profile,
        onStep: s => setStep(s),
      });
      setResult(out);
      if (out.refused.length) {
        toast.error(out.refused.length + ' of ' + EVERYTHING.length + ' tables could not be read. The file says which.');
      } else {
        toast.success('Saved ' + out.total.toLocaleString() + ' rows to ' + out.name);
      }
      await refresh();
    } catch (err) {
      toast.error('The copy did not finish: ' + (err?.message || String(err)));
    }
    setBusy(false);
    setStep(null);
  }

  return (
    <>
      {/* Rule 5: the live numbers sit on hairlines, not in tiles. Two by two
          below 640, where four figures and three rules do not fit across. */}
      <div className="figure-strip" style={{ paddingBottom: 14, borderBottom: '1px solid var(--border-soft)' }}>
        <Figure n={EVERYTHING.length} label="Tables" dot="var(--text-primary)" />
        <span className="tb-vr" style={{ margin: '0 14px' }} />
        {/* Rule 9: never taken is a dash, not a zero. A zero would read as a
            copy that came back empty, which is a different and worse thing. */}
        <Figure n={lastRows === null ? '-' : lastRows} label="Rows last time" dot={last ? 'var(--success)' : 'var(--critical)'} />
        <span className="tb-vr" style={{ margin: '0 14px' }} />
        <Figure n={age.days === null ? '-' : age.days} label="Days since" dot={age.stale ? 'var(--critical-ink)' : 'var(--success)'} />
      </div>

      {last === undefined ? (
        <Note tone="gold">Looking up when the last copy was taken...</Note>
      ) : (
        <Note tone={age.stale ? 'bad' : 'good'}>
          {age.sentence}
          {last?.user_name ? ' Taken by ' + last.user_name + '.' : ''}
          {age.stale && ' A copy is worth only as much as its freshness. Take one after a count cycle closes, after a catalog is loaded, and monthly whatever else has happened.'}
        </Note>
      )}

      <div style={{ marginTop: 18, fontSize: 13, lineHeight: 1.65, color: 'var(--text-secondary)', maxWidth: '62ch' }}>
        <p style={{ margin: '0 0 10px' }}>
          This writes one spreadsheet holding every table in this site, one sheet each, with a
          first page listing what should be in it. Keep it somewhere that is not Supabase and
          not only this laptop.
        </p>
        <p style={{ margin: '0 0 10px' }}>
          It does <b>not</b> hold the sign-in accounts or the uploaded logos. Those live in
          separate systems, and the backups guide in this project is how they are fetched.
        </p>
        <p style={{ margin: 0 }}>
          It holds what you are allowed to see. That is everything in this site, with one
          exception: what each person has set for themselves, like the width of a column,
          stays with that person.
        </p>
      </div>

      {busy && step && (
        <Note tone="gold">Reading {step.label}... ({step.index + 1} of {step.total})</Note>
      )}

      {result && (
        <Note tone={result.refused.length ? 'bad' : 'good'}>
          Saved {result.total.toLocaleString()} rows as {result.name}.
          {result.refused.length
            ? ' ' + result.refused.length + ' could not be read: ' + result.refused.map(r => r.label).join(', ') + '.'
            : ' Every table was read.'}
        </Note>
      )}

      <div className="btn-row" style={{ marginTop: 18 }}>
        <button className="btn btn-primary" onClick={take} disabled={busy || readOnly}>
          {busy ? 'Taking the copy...' : 'Take everything out'}
        </button>
      </div>
    </>
  );
}

/* ---------------------- How long things are kept ---------------------- */

export function RetentionPane({ readOnly, toast }) {
  const [years, setYears] = useState(DEFAULT_YEARS);
  const [typed, setTyped] = useState(String(DEFAULT_YEARS));
  const [due, setDue]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [asking, setAsking]   = useState(false);
  // Type the site's name first (question 24, 21 September 2026).
  const confirm = useTypeToConfirm();
  const [running, setRunning] = useState(false);
  const [done, setDone]       = useState(null);
  const [problem, setProblem] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const y = await loadYears();
    setYears(y.years);
    setTyped(String(y.years));
    const d = await loadDue();
    if (d.error) { setProblem(d.error); setDue(null); } else { setProblem(null); setDue(d.rows); }
    setLoading(false);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  async function onSaveYears() {
    setSaving(true);
    const out = await saveYears(typed);
    setSaving(false);
    if (!out.ok) { toast.error(out.error); return; }
    toast.success('Saved');
    await refresh();
  }

  async function onRun() {
    setRunning(true);
    const out = await runRemoval();
    setRunning(false);
    setAsking(false);
    if (out.error) { toast.error('Nothing was removed: ' + out.error); return; }
    setDone(out.rows);
    toast.success(removedSentence(out.rows));
    await refresh();
  }

  const total = totalDue(due);
  const rows  = (due || []).filter(r => Number(r.n) > 0);

  return (
    <>
      <div className="ua-fields" style={{ marginBottom: 18 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
          <span className="fld-label">Kept for</span>
          <input
            className="fld" type="number" min="1" max="99" value={typed} disabled={readOnly}
            onChange={e => setTyped(e.target.value)}
            style={{ maxWidth: 140 }}
          />
          <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
            Years. Three unless this site says otherwise, and never less than one.
          </span>
        </label>
      </div>

      {!readOnly && String(years) !== String(typed) && (
        <div className="btn-row" style={{ marginBottom: 18 }}>
          <button className="btn btn-primary" onClick={onSaveYears} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button className="btn btn-quiet" onClick={() => setTyped(String(years))} disabled={saving}>Put it back</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 22, paddingTop: 4 }}>
        <Bullets title={'Removed after ' + years + ' years'} items={REMOVED} dot="var(--critical)" />
        <Bullets title="Kept, however old" items={KEPT} dot="var(--success)" />
      </div>

      <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--border-soft)' }}>
        {loading ? (
          <Note tone="gold">Counting what is over the line...</Note>
        ) : problem ? (
          <Note tone="bad">Could not read what is due: {problem}</Note>
        ) : (
          <>
            <Note tone={total ? 'gold' : 'good'}>{dueSentence(due, years)}</Note>
            {rows.length > 0 && (
              <div style={{ marginTop: 14 }}>
                {rows.map(r => (
                  <div key={r.what} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    padding: '9px 0', borderBottom: '1px solid var(--border-soft)', fontSize: 13,
                  }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{r.what}</span>
                    <b style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{Number(r.n).toLocaleString()}</b>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {done && (
          <Note tone="good">
            {removedSentence(done)} It is written in the audit log, under Reports.
          </Note>
        )}

        {/* Rule 33: the act is honestly unavailable rather than hidden, so it is
            plain that it is waiting on there being something to remove. */}
        <div className="btn-row" style={{ marginTop: 18 }}>
          <button
            className="btn btn-danger"
            onClick={() => setAsking(true)}
            disabled={readOnly || loading || !!problem || total === 0}
          >
            Remove what is past {years} years
          </button>
        </div>

        <div style={{ marginTop: 14, fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-secondary)', maxWidth: '62ch' }}>
          {/* It said "Nothing is removed on its own" for twelve days after the
              nightly run went live (the audit after the rebrand, issue 24). */}
          This also happens on its own every night. Pressing this does it now. Either way it cannot
          be undone, so take a full copy first.
        </div>
      </div>

      {asking && (
        <Dialog
          kind="danger"
          icon={<IconTrash />}
          title={'Remove ' + total.toLocaleString() + ' ' + (total === 1 ? 'row' : 'rows') + '?'}
          says="This cannot be undone. There is no copy you can restore from unless you took one."
          size="question"
          onClose={() => (running ? null : setAsking(false))}
          footer={
            <>
              <button className="btn btn-quiet" onClick={() => setAsking(false)} disabled={running}>Keep it</button>
              <button className="btn btn-danger" onClick={onRun} disabled={running || !confirm.ready}>
                {running ? 'Removing...' : 'Remove it'}
              </button>
            </>
          }
        >
          <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)' }}>
            Everything below is more than {years} years old.
          </div>
          <div style={{ marginTop: 12 }}>
            {rows.map(r => (
              <div key={r.what} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                padding: '8px 0', borderBottom: '1px solid var(--border-soft)', fontSize: 13,
              }}>
                <span style={{ color: 'var(--text-secondary)' }}>{r.what}</span>
                <b style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{Number(r.n).toLocaleString()}</b>
              </div>
            ))}
          </div>

          {confirm.field}
        </Dialog>
      )}
    </>
  );
}
